// ===========================================
// INVOICE SCAN → RECEIVE STOCK
// ===========================================
// Photograph a supplier invoice; the AI reads its line items, we auto-match
// them to inventory (fuzzy name match), the manager confirms quantities and
// costs, and stock is received in one tap. Unmatched lines can create items.

import { useRef, useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { X, ScanLine, Loader2, Check, Camera } from 'lucide-react';
import { invoicesApi, inventoryApi } from '../services/api';

interface InventoryItemLite {
  id: string;
  name: string;
  unitOfMeasure: string;
}

interface ScanLineItem {
  name: string;
  quantity: number;
  unit: string;
  unitCostCents: number;
  itemId: string | ''; // '' = unmatched; 'NEW' = create
}

// Fuzzy match: exact > startsWith > token overlap
function matchItem(desc: string, items: InventoryItemLite[]): string {
  const d = desc.toLowerCase().trim();
  if (!d) return '';
  const exact = items.find((i) => i.name.toLowerCase() === d);
  if (exact) return exact.id;
  const starts = items.find(
    (i) => i.name.toLowerCase().startsWith(d) || d.startsWith(i.name.toLowerCase())
  );
  if (starts) return starts.id;
  const dTokens = new Set(d.split(/\s+/).filter((t) => t.length > 2));
  let best: { id: string; score: number } | null = null;
  for (const i of items) {
    const tokens = i.name.toLowerCase().split(/\s+/);
    const score = tokens.filter((t) => dTokens.has(t)).length;
    if (score > 0 && (!best || score > best.score)) best = { id: i.id, score };
  }
  return best?.id || '';
}

export default function InvoiceScanReceiveModal({
  items,
  onClose,
}: {
  items: InventoryItemLite[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [lines, setLines] = useState<ScanLineItem[] | null>(null);

  const handleFile = async (file: File) => {
    setScanning(true);
    setError('');
    try {
      const res = await invoicesApi.parse(file);
      const parsed = res.data?.data?.parsed;
      if (!parsed?.lineItems?.length) {
        setError("Couldn't read line items from that photo — try a clearer, flatter shot.");
        return;
      }
      setVendorName(parsed.vendorName || '');
      setInvoiceNumber(parsed.invoiceNumber || '');
      setLines(
        parsed.lineItems.map((li: any) => ({
          name: li.description || 'Unknown item',
          quantity: Number(li.quantity) || 1,
          unit: li.unit || '',
          unitCostCents: Number(li.unitPriceCents) || 0,
          itemId: matchItem(li.description || '', items),
        }))
      );
    } catch {
      setError("Couldn't read that photo — try again with better lighting.");
    } finally {
      setScanning(false);
    }
  };

  const receiveMutation = useMutation({
    mutationFn: async () => {
      const payload = (lines || [])
        .filter((l) => l.itemId !== '' && l.quantity > 0)
        .map((l) => ({
          itemId: l.itemId === 'NEW' ? undefined : l.itemId,
          createNew: l.itemId === 'NEW',
          name: l.name,
          quantity: l.quantity,
          unit: l.unit || undefined,
          unitCostCents: l.unitCostCents || undefined,
        }));
      if (payload.length === 0) throw new Error('Match at least one line to an item first.');
      const res = await inventoryApi.receiveFromInvoice({
        lines: payload,
        vendorName: vendorName || undefined,
        invoiceNumber: invoiceNumber || undefined,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      onClose();
    },
    onError: (e: any) => {
      setError(e?.response?.data?.error?.message || e?.message || "Couldn't receive the stock.");
    },
  });

  const updateLine = (i: number, patch: Partial<ScanLineItem>) => {
    setLines((prev) => prev!.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  };

  const matchedCount = (lines || []).filter((l) => l.itemId !== '').length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90dvh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-primary-600" />
            Scan invoice into inventory
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {!lines ? (
            <div className="text-center py-8">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              />
              <Camera className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-1 font-medium">Photograph the supplier invoice</p>
              <p className="text-sm text-slate-400 mb-5">
                Line items are read automatically and matched to your inventory — you confirm before anything changes.
              </p>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={scanning}
                className="btn btn-primary inline-flex items-center gap-2"
              >
                {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {scanning ? 'Reading invoice…' : 'Take / choose photo'}
              </button>
              {error && <p className="text-sm text-red-600 mt-4">{error}</p>}
            </div>
          ) : (
            <>
              {(vendorName || invoiceNumber) && (
                <p className="text-sm text-slate-500 mb-3">
                  {vendorName}{invoiceNumber ? ` — #${invoiceNumber}` : ''}
                </p>
              )}
              <div className="space-y-3">
                {lines.map((l, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-xl space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-slate-800 text-sm flex-1 min-w-0 truncate">{l.name}</p>
                      <select
                        value={l.itemId}
                        onChange={(e) => updateLine(i, { itemId: e.target.value })}
                        className="input py-1.5 text-sm w-44"
                      >
                        <option value="">Skip this line</option>
                        <option value="NEW">＋ Create new item</option>
                        {items.map((it) => (
                          <option key={it.id} value={it.id}>{it.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <label className="flex items-center gap-1.5 text-slate-500">
                        Qty
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={l.quantity}
                          onChange={(e) => updateLine(i, { quantity: parseFloat(e.target.value) || 0 })}
                          className="input py-1 w-20"
                        />
                        <span className="text-slate-400">{l.unit}</span>
                      </label>
                      <label className="flex items-center gap-1.5 text-slate-500 ml-auto">
                        Unit cost $
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={l.unitCostCents ? (l.unitCostCents / 100).toFixed(2) : ''}
                          onChange={(e) =>
                            updateLine(i, { unitCostCents: Math.round(parseFloat(e.target.value || '0') * 100) })
                          }
                          className="input py-1 w-24"
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
            </>
          )}
        </div>

        {lines && (
          <div className="p-4 border-t border-slate-200 flex items-center justify-between gap-3 flex-shrink-0">
            <button
              onClick={() => { setLines(null); setError(''); }}
              className="btn btn-secondary"
            >
              Rescan
            </button>
            <button
              onClick={() => receiveMutation.mutate()}
              disabled={matchedCount === 0 || receiveMutation.isPending}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {receiveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Receive {matchedCount} line{matchedCount === 1 ? '' : 's'} into stock
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
